import { useRef } from "react";
import "./ControlField.css";

const FIELD_W   = 450;
const FIELD_H   = 350;
const PAD       = 20;
const INNER_W   = FIELD_W - 2 * PAD;   // playable width in SVG units
const INNER_H   = FIELD_H - 2 * PAD;   // playable height in SVG units
const PAD_FRAC_X = PAD / FIELD_W;
const PAD_FRAC_Y = PAD / FIELD_H;

// x ∈ [0,1] lateral  → CSS left %
// y ∈ [0,0.5] depth  → CSS top %  (y=0 goal → bottom, y=0.5 centre line → top)
const toPctX = (r) => (r * (1 - 2 * PAD_FRAC_X) + PAD_FRAC_X) * 100;
const toPctY = (r) => ((1 - 2 * r) * (1 - 2 * PAD_FRAC_Y) + PAD_FRAC_Y) * 100;

// The SVG centre arc has radius 60px. x and y use different scales (x: 1 unit = INNER_W px,
// y: 1 unit = 2*INNER_H px), so the exclusion zone is an ellipse in logical coordinates.
const SEMI_RX = 60 / INNER_W;           // x semi-axis
const SEMI_RY = 60 / (2 * INNER_H);     // y semi-axis

// Centre circle is at engine (0.5, 0.5); push players out of it, keeping y ≤ 0.5.
function pushOutOfSemicircle(rx, ry) {
  const dx = rx - 0.5;
  const dy = ry - 0.5;
  const nx = dx / SEMI_RX;
  const ny = dy / SEMI_RY;
  const distN = Math.sqrt(nx * nx + ny * ny);
  if (distN >= 1)     return [rx, ry];
  if (distN < 0.001)  return [0.5, 0.5 - SEMI_RY];
  return [Math.max(0, Math.min(1, 0.5 + (nx / distN) * SEMI_RX)), 0.5 + (ny / distN) * SEMI_RY];
}

/**
 * @param {{
 *   players: Array<{id, x, y, tacticId, isStarter}>,
 *   selectedId: number | null,
 *   teamColor: string,
 *   background: string,
 *   onSelect: (id: number) => void,
 *   onMove: (id: number, x: number, y: number) => void,
 * }} props
 */
export default function ControlField({ players, tactics = [], selectedId, teamColor, background, onSelect, onMove }) {
  const containerRef = useRef(null);
  const dragRef      = useRef(null);

  // Resolve a player's assigned tactic id → its name, shown under the player
  // name on the pitch (so identical player names are still distinguishable and
  // you can see each player's tactic at a glance).
  const tacticNameById = new Map(tactics.map((t) => [t.id, t.name]));

  const handlePointerDown = (player) => (e) => {
    e.preventDefault();
    onSelect(player.frontendId);

    const rect = containerRef.current.getBoundingClientRect();
    dragRef.current = {
      id:      player.frontendId,
      startCX: e.clientX,
      startCY: e.clientY,
      startRx: player.x,
      startRy: player.y,
      fw:      rect.width  * (1 - 2 * PAD_FRAC_X),
      fh:      rect.height * (1 - 2 * PAD_FRAC_Y),
    };
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dpx = e.clientX - drag.startCX;
    const dpy = e.clientY - drag.startCY;
    let rx = Math.max(0,   Math.min(1,   drag.startRx + dpx / drag.fw));
    // y ∈ [0,0.5] — half the coordinate range, so full-height drag = 0.5 delta
    let ry = Math.max(0,   Math.min(0.5, drag.startRy - dpy / (2 * drag.fh)));
    [rx, ry] = pushOutOfSemicircle(rx, ry);
    onMove(drag.id, rx, ry);
  };

  const handlePointerUp = () => { dragRef.current = null; };

  return (
    <div className="fieldWrapper">
      <div
        ref={containerRef}
        className="field"
        style={{ backgroundImage: `url(${background})` }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        <svg className="fieldSvg" viewBox={`0 0 ${FIELD_W} ${FIELD_H}`} preserveAspectRatio="xMidYMid meet">
          <g transform={`translate(${PAD},${PAD})`}>
            {/* crispEdges: snap the long straight boundary to the pixel grid so
                a fractional scale factor (e.g. viewBox 450 → 420px on tablet, at
                high DPR) doesn't spread the thin stroke into a fuzzy grey band. */}
            <rect x={0} y={0} width={INNER_W} height={INNER_H} stroke="white" strokeWidth="2" fill="none" shapeRendering="crispEdges" />
            {/* Centre arc (D) */}
            <path
              d={`M ${INNER_W/2 - 60} 0 A 60 60 0 0 0 ${INNER_W/2 + 60} 0`}
              stroke="white" strokeWidth="2" fill="rgba(255,255,255,0.08)"
            />
            {(() => {
              // Penalty area (official) — 58 % wide, depth = 16 % of the field
              // LENGTH = 32 engine units. INNER_H spans the half-field (100
              // engine units), so 32 units = 0.32 of INNER_H.
              const paW = Math.round(INNER_W * 0.58);
              const paX = Math.round((INNER_W - paW) / 2);
              const paH = Math.round(INNER_H * 0.32);

              // Goal — 20 % of field width, protrudes 16 px below boundary
              const gw = Math.round(INNER_W * 0.20);
              const gx = Math.round((INNER_W - gw) / 2);
              const gy = INNER_H;
              const gh = 16;
              const netCols = 4, netRows = 3;

              return (
                <g>
                  {/* Penalty area */}
                  <rect x={paX} y={INNER_H - paH} width={paW} height={paH}
                    stroke="rgba(255,255,255,0.8)" strokeWidth="1.5" fill="none"
                    shapeRendering="crispEdges" />

                  {/* Goal net */}
                  {Array.from({ length: netCols - 1 }, (_, i) => (
                    <line key={`nc${i}`}
                      x1={gx + (i + 1) * gw / netCols} y1={gy}
                      x2={gx + (i + 1) * gw / netCols} y2={gy + gh}
                      stroke="rgba(255,255,255,0.35)" strokeWidth="0.7" />
                  ))}
                  {Array.from({ length: netRows - 1 }, (_, i) => (
                    <line key={`nr${i}`}
                      x1={gx} y1={gy + (i + 1) * gh / netRows}
                      x2={gx + gw} y2={gy + (i + 1) * gh / netRows}
                      stroke="rgba(255,255,255,0.35)" strokeWidth="0.7" />
                  ))}
                  {/* Goal frame */}
                  <line x1={gx}      y1={gy} x2={gx}      y2={gy + gh} stroke="white" strokeWidth="2" strokeLinecap="round" />
                  <line x1={gx + gw} y1={gy} x2={gx + gw} y2={gy + gh} stroke="white" strokeWidth="2" strokeLinecap="round" />
                  <line x1={gx}      y1={gy} x2={gx + gw} y2={gy}      stroke="white" strokeWidth="2" strokeLinecap="round" />
                  <line x1={gx}      y1={gy + gh} x2={gx + gw} y2={gy + gh} stroke="white" strokeWidth="2" strokeLinecap="round" />
                </g>
              );
            })()}
          </g>
        </svg>

        {players.map((p) => {
          const selected   = p.frontendId === selectedId;
          const noTactic   = p.tacticId === null;
          let cls = "player-dot";
          if (selected) cls += " player-selected";
          if (noTactic)  cls += " player-no-tactic";

          return (
            <div
              key={p.frontendId}
              className="player-wrapper"
              style={{ left: `${toPctX(p.x)}%`, top: `${toPctY(p.y)}%` }}
            >
              <div className={cls} style={{ backgroundColor: teamColor }} onPointerDown={handlePointerDown(p)}>
                {p.name && <span className="player-number">{p.name[0].toUpperCase()}</span>}
                {p.isStarter && <span className="starter-badge">★</span>}
                {p.isGoalkeeper && <span className="goalkeeper-badge">🧤</span>}
              </div>
              {p.name && <span className="player-name-label">{p.name}</span>}
              {p.tacticId != null && tacticNameById.has(p.tacticId) && (
                <span className="player-tactic-label">{tacticNameById.get(p.tacticId)}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
